import { fixed, trim } from '../../math/format';
import { nearestDrill } from '../../math/drills';
import { inchToMm, mmToInch } from '../../math/units';
import { mcOptions } from '../helpers';
import type { QuestionTemplate } from '../types';

export const INMM_TEMPLATES: readonly QuestionTemplate[] = [
  {
    id: 'inmm.toMm',
    topicId: 'inmm',
    skill: 'Inches to millimetres',
    gen: (rng) => {
      const value = rng.step(0.125, 3, 0.125);
      const solved = inchToMm(value);
      const correct = `${fixed(solved.value, 2)} mm`;

      return {
        kind: 'mc',
        prompt: `${trim(value, 3)}" in millimetres?`,
        formula: 'in × 25.4 = mm',
        why: `${trim(value, 3)} × 25.4 = ${fixed(solved.value, 2)}. Multiplying by 25.4 always makes the number bigger.`,
        steps: solved.steps,
        ...mcOptions(rng, correct, [
          {
            label: `${fixed(value / 25.4, 3)} mm`,
            trap: 'You divided. Going to millimetres the number has to get bigger, not smaller.',
          },
          {
            label: `${fixed(value * 2.54, 3)} mm`,
            trap: 'That is 2.54, the centimetre conversion. Millimetres use 25.4.',
          },
          {
            // Transposing the digits of 25.4 is a real and very common slip,
            // and unlike a decimal-point error it is not just the centimetre
            // conversion wearing a different hat.
            label: `${fixed(value * 24.5, 2)} mm`,
            trap: 'The digits are transposed. The constant is 25.4, not 24.5.',
          },
        ]),
      };
    },
  },
  {
    id: 'inmm.toInch',
    topicId: 'inmm',
    skill: 'Millimetres to inches',
    gen: (rng) => {
      const value = rng.step(3, 40, 0.5);
      const solved = mmToInch(value);
      const correct = `${fixed(solved.value, 4)}"`;

      return {
        kind: 'mc',
        prompt: `${trim(value, 1)} mm in inches?`,
        formula: 'mm ÷ 25.4 = in',
        why: `${trim(value, 1)} ÷ 25.4 = ${fixed(solved.value, 4)}". Going to inches the number always gets smaller.`,
        steps: solved.steps,
        ...mcOptions(rng, correct, [
          {
            label: `${fixed(value * 25.4, 2)}"`,
            trap: 'You multiplied. An inch is bigger than a millimetre, so the count of inches has to be smaller.',
          },
          {
            label: `${fixed(value / 2.54, 3)}"`,
            trap: 'That is 2.54, the centimetre conversion. Millimetres use 25.4.',
          },
          {
            label: `${fixed(value / 24.5, 4)}"`,
            trap: 'The digits are transposed. The constant is 25.4, not 24.5.',
          },
        ]),
      };
    },
  },
  {
    id: 'inmm.drillPick',
    topicId: 'inmm',
    skill: 'Picking a drill for a metric callout',
    gen: (rng) => {
      const mmSize = rng.pick([5, 6, 8, 10, 12, 14, 16, 20]);
      const solved = mmToInch(mmSize);
      const match = nearestDrill(solved.value, 'fraction');
      const oneBigger = nearestDrill(solved.value + 0.03, 'fraction');
      const oneSmaller = nearestDrill(solved.value - 0.03, 'fraction');

      return {
        kind: 'mc',
        prompt: `The print says ${mmSize} mm. Which fractional drill is closest?`,
        formula: 'mm ÷ 25.4 = in',
        why: `${mmSize} ÷ 25.4 = ${fixed(solved.value, 4)}", and ${match.drill.label} is ${fixed(match.drill.inches, 4)}" — the nearest line on the index.`,
        steps: [
          ...solved.steps,
          {
            kind: 'result' as const,
            text: `Nearest fractional drill: ${match.drill.label} at ${fixed(match.drill.inches, 4)}"`,
          },
        ],
        ...mcOptions(rng, match.drill.label, [
          {
            label: oneBigger.drill.label,
            trap: `That one is ${fixed(oneBigger.drill.inches - solved.value, 4)}" oversize. ${match.drill.label} lands closer.`,
          },
          {
            label: oneSmaller.drill.label,
            trap: `That one is ${fixed(solved.value - oneSmaller.drill.inches, 4)}" undersize. ${match.drill.label} lands closer.`,
          },
          {
            // Converting twice. The 2.54 error would be the obvious third
            // option here, but for anything over about 25 mm it saturates at
            // the biggest drill in the rack and stops being a real choice.
            label: nearestDrill(solved.value / 25.4, 'fraction').drill.label,
            trap: 'You divided by 25.4 twice. One conversion takes you from millimetres to inches — doing it again takes you somewhere meaningless.',
          },
        ]),
      };
    },
  },
  {
    id: 'inmm.slider',
    topicId: 'inmm',
    skill: 'Feeling the size of a metric callout',
    gen: (rng) => {
      const mmSize = rng.pick([10, 15, 20, 25, 30, 40]);
      const solved = mmToInch(mmSize);

      return {
        kind: 'slider',
        prompt: `Drag to roughly ${mmSize} mm.`,
        sub: 'The scale is in inches.',
        min: 0,
        max: 2,
        stepSize: 0.01,
        answer: solved.value,
        tolerance: 0.06,
        unit: '"',
        convert: (v: number) => `${fixed(v * 25.4, 1)} mm`,
        why: `${mmSize} ÷ 25.4 = ${fixed(solved.value, 3)}". An inch is a hair over 25 mm, which is the whole reason 25.4 matters.`,
        steps: solved.steps,
      };
    },
  },
];
